/* ============================================================================
 * Supabase Storage helpers for product label images (and other small
 * binary assets we don't want bloating JSONB rows).
 *
 * Public bucket convention: `product-labels`. Make it public so we can
 * call `getPublicUrl` without minting signed URLs every render. The
 * bucket holds one object per product (or any caller-chosen path).
 *
 * Bucket bootstrap (run once in the Supabase SQL editor):
 *   insert into storage.buckets (id, name, public)
 *   values ('product-labels', 'product-labels', true)
 *   on conflict (id) do nothing;
 *
 * If the bucket doesn't exist yet, uploadDataUrlToStorage throws —
 * the product save handler logs the error and keeps the in-memory
 * data URL so the user can still see the image during the session.
 * ============================================================================ */

import { supabase } from "./supabaseClient";

/* Strip the "data:<mime>;base64," prefix and decode the rest into a
   Blob. Returns null when the input doesn't look like a data URL. */
function dataUrlToBlob(dataUrl) {
  if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) return null;
  const [meta, b64] = dataUrl.split(",");
  if (!b64) return null;
  const mime = (meta.match(/data:([^;]+)/) || [])[1] || "application/octet-stream";
  const bytes = atob(b64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  return new Blob([buf], { type: mime });
}

/* Guess a sensible file extension from a mime type. Covers the upload
   surface across product labels (images), shipment docs (PDFs, office
   docs), and any other binary we route through Storage. */
function extForMime(mime) {
  if (!mime) return "bin";
  const m = String(mime).toLowerCase();
  if (m.includes("png"))  return "png";
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("webp")) return "webp";
  if (m.includes("gif"))  return "gif";
  if (m.includes("svg"))  return "svg";
  if (m.includes("pdf"))  return "pdf";
  if (m.includes("msword") || m.includes("wordprocessingml")) return "docx";
  if (m.includes("excel") || m.includes("spreadsheetml")) return "xlsx";
  if (m.includes("plain")) return "txt";
  if (m.includes("csv")) return "csv";
  return "bin";
}

/* Upload a data URL to Supabase Storage. `bucket` is the bucket id
   (must be created up-front via the SQL above). `pathBase` is the
   filename without extension — we append the extension based on the
   data URL's mime type so the resulting object is browser-renderable.
   Returns the public URL on success. `upsert:true` so re-saving the
   same product replaces the file instead of erroring. */
export async function uploadDataUrlToStorage(bucket, pathBase, dataUrl) {
  const blob = dataUrlToBlob(dataUrl);
  if (!blob) throw new Error("uploadDataUrlToStorage: not a data URL");
  const ext = extForMime(blob.type);
  const path = String(pathBase) + "." + ext;
  const { error: upErr } = await supabase.storage
    .from(bucket)
    .upload(path, blob, { upsert: true, contentType: blob.type, cacheControl: "3600" });
  if (upErr) throw upErr;
  const pub = supabase.storage.from(bucket).getPublicUrl(path);
  return {
    path: path,
    publicUrl: (pub && pub.data && pub.data.publicUrl) || "",
  };
}

/* Resolve a stored object path back to a public URL. Returns "" if
   the helper is called with an empty path so callers can short-
   circuit on the empty string without a separate null check. */
export function publicUrlFor(bucket, path) {
  if (!bucket || !path) return "";
  const pub = supabase.storage.from(bucket).getPublicUrl(path);
  return (pub && pub.data && pub.data.publicUrl) || "";
}

/* Detect a "fresh upload that needs persisting" — the caller knows
   they're sending a data URL and should call uploadDataUrlToStorage
   instead of saving the URL directly to the row. */
export function isDataUrl(s) {
  return typeof s === "string" && s.startsWith("data:");
}

/* Upload a raw File / Blob to Supabase Storage. Skips the data-URL
   detour entirely (no FileReader, no base64 in browser memory). Use
   this for shipper-portal shipment documents where the upload surface
   gives us a File directly off the <input>/drop event.

   `bucket`  — bucket id (must exist; for shipper docs that's
               `shipment-docs`, see the SQL in the supabaseSchema
               comment block).
   `path`    — full storage key, including extension. Caller is
               responsible for namespacing
               (`{supplier_id}/{embarque_id}/...`).
   `file`    — File or Blob.

   Returns { path, publicUrl }. With a public bucket publicUrl works
   directly in <a href> / window.open. For a private bucket the
   caller can use `path` to mint a signed URL via createSignedUrlFor
   below. `upsert:true` means re-uploading the same path overwrites
   the previous version (necessary for single-slot fields like the
   shipper invoice file, where the shipper may replace their first
   pick before submitting). */
export async function uploadFileToStorage(bucket, path, file) {
  if (!file) throw new Error("uploadFileToStorage: file is required");
  const contentType = file.type || "application/octet-stream";
  const { error: upErr } = await supabase.storage
    .from(bucket)
    .upload(path, file, { upsert: true, contentType: contentType, cacheControl: "3600" });
  if (upErr) throw upErr;
  const pub = supabase.storage.from(bucket).getPublicUrl(path);
  return {
    path: path,
    publicUrl: (pub && pub.data && pub.data.publicUrl) || "",
  };
}

/* Mint a time-limited signed URL for a Storage object. Use this when
   the bucket is PRIVATE (RLS-protected) and you need a URL the
   browser can fetch for a short window. Returns "" on missing
   inputs or on error so callers can guard with a truthy check. */
export async function createSignedUrlFor(bucket, path, ttlSec) {
  if (!bucket || !path) return "";
  const ttl = typeof ttlSec === "number" && ttlSec > 0 ? ttlSec : 3600;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, ttl);
  if (error || !data) return "";
  return data.signedUrl || "";
}

/* Delete a Storage object by path. No-ops on empty path. Used when
   the shipper removes a previously-uploaded doc from an embarque so
   we don't leak orphan files in the bucket. */
export async function removeFromStorage(bucket, path) {
  if (!bucket || !path) return;
  try { await supabase.storage.from(bucket).remove([path]); }
  catch (e) { /* best-effort; orphans are tolerable */ }
}
